import type { OnboardingBookingSlot, OnboardingDateItem, OnboardingTimeGroup } from '@features/trial-booking/model'

export type JpTrialBookingError =
  | 'SLOT_UNAVAILABLE'
  | 'TOO_SOON'
  | 'ALREADY_BOOKED'
  | 'EMAIL_VERIFICATION_REQUIRED'
  | 'RETRYABLE'
  | 'FATAL'

export type JpTrialLessonLanguage = 'en' | 'ko'

export type JpTrialBookingOptions = {
  dates: OnboardingDateItem[]
  timeGroupsByDate: Record<string, OnboardingTimeGroup[]>
  recommendedSlots: OnboardingBookingSlot[]
}

export const JP_TRIAL_SLOT_RANGE_FEATURE_KEY = 'jp_trial_slot_range'

const JP_TRIAL_BOOKING_RANGE_DAYS = 7
const JP_TRIAL_BOOKING_RANGE_STEPS = [2, 4, 6, 7] as const
const isWholeHourSlot = ({ time }: { time: string }) => time.endsWith(':00')

export const normalizeJpTrialInitialRangeDays = (value: unknown, hasAuthenticatedUser: boolean): number => {
  if (!hasAuthenticatedUser || typeof value !== 'number' || !Number.isInteger(value)) {
    return JP_TRIAL_BOOKING_RANGE_DAYS
  }

  return Math.min(Math.max(value, 1), JP_TRIAL_BOOKING_RANGE_DAYS)
}

export const getNextJpTrialRangeDays = (currentRangeDays: number): number =>
  JP_TRIAL_BOOKING_RANGE_STEPS.find((rangeDays) => rangeDays > currentRangeDays) ?? JP_TRIAL_BOOKING_RANGE_DAYS

export const filterJpTrialExperimentFeatureIds = (featureIds: string[]): string[] =>
  featureIds.filter((featureId) => featureId !== JP_TRIAL_SLOT_RANGE_FEATURE_KEY)

export type JpTrialSlotEmptyReason = 'all_booked' | 'no_tutor' | 'ticket_not_yet_valid' | 'penalty_holding'

const readSlotStatuses = (options: JpTrialBookingOptions) =>
  Object.values(options.timeGroupsByDate)
    .flat()
    .flatMap((group) => group.times.map((slot) => slot.status))

export const getJpTrialSlotEmptyReason = (
  displayOptions: JpTrialBookingOptions,
  sourceOptions: JpTrialBookingOptions = displayOptions,
): JpTrialSlotEmptyReason => {
  const sourceStatuses = readSlotStatuses(sourceOptions)

  if (sourceStatuses.some((status) => status === 'penalty' || status === 'holding')) {
    return 'penalty_holding'
  }
  if (sourceStatuses.includes('too-soon')) {
    return 'ticket_not_yet_valid'
  }
  return readSlotStatuses(displayOptions).length > 0 ? 'all_booked' : 'no_tutor'
}

export const applyJpTrialRangePolicy = (
  options: JpTrialBookingOptions,
  rangeDays = JP_TRIAL_BOOKING_RANGE_DAYS,
): JpTrialBookingOptions => {
  const safeRangeDays = Math.min(Math.max(Math.floor(rangeDays), 1), JP_TRIAL_BOOKING_RANGE_DAYS)
  const dates = options.dates.slice(0, safeRangeDays)
  const visibleDateKeys = new Set(dates.map(({ fullDate }) => fullDate))

  return {
    ...options,
    dates,
    timeGroupsByDate: Object.fromEntries(
      dates.map(({ fullDate }) => [fullDate, options.timeGroupsByDate[fullDate] ?? []]),
    ),
    recommendedSlots: options.recommendedSlots.filter(({ fullDate }) => visibleDateKeys.has(fullDate)),
  }
}

export const applyJpTrialSlotPolicy = (
  options: JpTrialBookingOptions,
  language: JpTrialLessonLanguage,
  rangeDays = JP_TRIAL_BOOKING_RANGE_DAYS,
): JpTrialBookingOptions => {
  const rangeCompliantOptions = applyJpTrialRangePolicy(options, rangeDays)
  const isLanguageCompliantSlot = language === 'ko' ? isWholeHourSlot : () => true
  const timeGroupsByDate = Object.fromEntries(
    rangeCompliantOptions.dates.map(({ fullDate }) => [
      fullDate,
      (rangeCompliantOptions.timeGroupsByDate[fullDate] ?? [])
        .map((group) => ({
          ...group,
          times: group.times.filter(isLanguageCompliantSlot),
        }))
        .filter((group) => group.times.length > 0),
    ]),
  )

  return {
    ...rangeCompliantOptions,
    timeGroupsByDate,
    recommendedSlots: rangeCompliantOptions.recommendedSlots.filter(isLanguageCompliantSlot),
  }
}

const readErrorCode = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return ''
  }

  const value = error as {
    code?: unknown
    cause?: { resultCdName?: unknown; message?: unknown }
    message?: unknown
  }

  return [value.code, value.cause?.resultCdName, value.cause?.message, value.message]
    .filter((item): item is string => typeof item === 'string')
    .join(' ')
    .toUpperCase()
}

export const classifyJpTrialBookingError = (error: unknown): JpTrialBookingError => {
  const code = readErrorCode(error)

  if (/INVALID_TWO_HOUR_BEFORE|INVALID_TIME_PAST|TOO[_ -]?SOON/.test(code)) {
    return 'TOO_SOON'
  }
  if (/ALREADY.*(BOOK|TRIAL)|TRIAL_BOOKING_CLOSED|DUPLICATE_TRIAL/.test(code)) {
    return 'ALREADY_BOOKED'
  }
  if (/EMAIL_VERIFICATION_REQUIRED/.test(code)) {
    return 'EMAIL_VERIFICATION_REQUIRED'
  }
  if (/INVALID_RESERVATION_TIME|SCHEDULE.*(CLOSE|NOT_FOUND)|NO_TUTOR|SLOT/.test(code)) {
    return 'SLOT_UNAVAILABLE'
  }
  if (/PODO_API_TIMEOUT|PODO_API_NETWORK_ERROR|TIMEOUT|NETWORK/.test(code)) {
    return 'RETRYABLE'
  }
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status?: unknown }).status === 'number' &&
    (error as { status: number }).status >= 500
  ) {
    return 'RETRYABLE'
  }

  return 'FATAL'
}

export const sanitizeEnglishNickname = (value: string) =>
  value
    .replace(/[^A-Za-z ]/g, '')
    .replace(/\s{2,}/g, ' ')
    .slice(0, 20)

export const isValidEnglishNickname = (value: string) => /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(value)

export const hasCompletedJpTrialProfile = ({
  availableLanguages,
  language,
  nickname,
  emailValid,
  emailVerified,
}: {
  availableLanguages: readonly JpTrialLessonLanguage[]
  language: JpTrialLessonLanguage
  nickname: string
  emailValid: boolean
  emailVerified: boolean
}) => availableLanguages.includes(language) && isValidEnglishNickname(nickname) && emailValid && emailVerified

export const createBookingIntentKey = () => crypto.randomUUID()
